import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Eyebrow } from "@/components/Motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DOCTOR } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONCERNS = [
  ["Fracture / Trauma", "c1"],
  ["Joint Replacement", "c2"],
  ["Nonunion / Malunion", "c3"],
  ["Bone or Joint Infection", "c4"],
  ["Deformity Correction", "c5"],
  ["Second Opinion", "c6"],
  ["Other", "c7"],
];

const TIME_SLOTS = [
  ["Morning (10 AM – 1 PM)", "ts1", "morning"],
  ["Afternoon (2 PM – 5 PM)", "ts2", "afternoon"],
  ["Evening (5 PM – 8 PM)", "ts3", "evening"],
];

const SEX_OPTIONS = [
  ["Male", "sex_m"],
  ["Female", "sex_f"],
  ["Other", "sex_o"],
];

const EMPTY = {
  name: "",
  age: "",
  sex: "",
  phone: "",
  email: "",
  preferred_date: "",
  time_slot: "",
  concern: "",
  message: "",
};

const Book = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.time_slot || !form.concern || !form.sex) {
      toast.error(t("f_validation"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/appointments`, form);
      setConfirmed({ id: data.id, email_sent: data.email_sent });
      toast.success(t("toast_success"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : t("toast_error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="book-page">
      <PageHero
        key={lang}
        eyebrow={t("bk_hero_eyebrow")}
        lines={[t("bk_hero_l1"), t("bk_hero_l2")]}
        testId="book-hero"
      >
        {t("bk_hero_sub", { name: DOCTOR.name })}
      </PageHero>

      <section className="bg-bone pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* INFO PANEL */}
          <Reveal className="lg:col-span-4">
            <div className="lg:sticky lg:top-32 space-y-8">
              <div>
                <Eyebrow>{t("bk_info_eyebrow")}</Eyebrow>
                <div className="mt-6 space-y-5">
                  <a
                    href={DOCTOR.phoneHref}
                    data-testid="book-phone-link"
                    className="flex items-center gap-4 group"
                  >
                    <span className="w-11 h-11 rounded-full bg-pine-100 flex items-center justify-center text-pine-600 group-hover:bg-pine-500 group-hover:text-white transition-colors duration-300">
                      <Phone size={18} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.18em] text-sage">{t("bk_call_label")}</span>
                      <span className="block font-semibold text-pine-950">{DOCTOR.phoneDisplay}</span>
                    </span>
                  </a>
                  <a
                    href={DOCTOR.emailHref}
                    data-testid="book-email-link"
                    className="flex items-center gap-4 group"
                  >
                    <span className="w-11 h-11 rounded-full bg-pine-100 flex items-center justify-center text-pine-600 group-hover:bg-pine-500 group-hover:text-white transition-colors duration-300">
                      <Mail size={18} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.18em] text-sage">{t("bk_email_label")}</span>
                      <span className="block font-semibold text-pine-950 break-all">{DOCTOR.email}</span>
                      <span className="block font-semibold text-pine-950 break-all">{DOCTOR.emailHospital}</span>
                    </span>
                  </a>
                  <div className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-full bg-pine-100 flex items-center justify-center text-pine-600 shrink-0">
                      <Phone size={18} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.18em] text-sage">
                        Hospital Appointment Desk
                      </span>
                      {DOCTOR.appointmentLines.map((l) => (
                        <a
                          key={l.number}
                          href={l.href}
                          data-testid={`book-desk-line-${l.number.replace(/[^0-9]/g, "")}`}
                          className="block font-semibold text-pine-950 hover:text-pine-500 transition-colors"
                        >
                          {l.number}
                          {l.label === "Toll Free" && (
                            <span className="text-xs font-medium text-sage"> (Toll Free)</span>
                          )}
                        </a>
                      ))}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="w-11 h-11 rounded-full bg-pine-100 flex items-center justify-center text-pine-600">
                      <MapPin size={18} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.18em] text-sage">{t("bk_clinic_label")}</span>
                      <span className="block font-semibold text-pine-950">
                        {DOCTOR.hospital}, {DOCTOR.hospitalArea}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="w-11 h-11 rounded-full bg-pine-100 flex items-center justify-center text-pine-600">
                      <Clock size={18} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-[0.18em] text-sage">{t("bk_opd_label")}</span>
                      <span className="block font-semibold text-pine-950">{t("bk_opd1")}</span>
                      <span className="block font-semibold text-pine-950">{t("bk_opd2")}</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-pine-950 text-bone p-7" data-testid="emergency-note">
                <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-pine-300">
                  {t("bk_em_label")}
                </div>
                <p className="mt-3 text-sm text-pine-100/85 leading-relaxed">
                  {t("bk_em_body", { phone: DOCTOR.phoneDisplay })}
                </p>
              </div>
            </div>
          </Reveal>

          {/* FORM / CONFIRMATION */}
          <Reveal className="lg:col-span-8" delay={0.15}>
            {confirmed ? (
              <motion.div
                data-testid="booking-confirmation"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="bg-white border border-mist p-10 lg:p-14"
              >
                <CheckCircle2 size={52} className="text-pine-500" strokeWidth={1.4} />
                <h2 className="mt-6 font-serif font-light text-4xl tracking-tight text-pine-950">
                  {t("ok_title")}
                </h2>
                <p className="mt-4 text-sage leading-relaxed max-w-lg">
                  {t("ok_body", {
                    name: form.name,
                    date: form.preferred_date,
                    slot: form.time_slot,
                    phone: form.phone,
                  })}
                  {confirmed.email_sent ? t("ok_email_note") : ""}
                </p>
                <div className="mt-6 text-xs uppercase tracking-[0.18em] text-sage">
                  {t("ok_ref")} <span className="text-pine-900 font-semibold">{confirmed.id.slice(0, 8).toUpperCase()}</span>
                </div>
                <button
                  onClick={() => {
                    setConfirmed(null);
                    setForm(EMPTY);
                  }}
                  data-testid="book-another-button"
                  className="group mt-10 inline-flex items-center gap-2 font-semibold text-sm text-pine-900 hover:text-pine-500 transition-colors duration-300"
                >
                  {t("ok_another")}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="booking-form"
                className="bg-white border border-mist p-8 lg:p-12"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                  <div className="space-y-2.5">
                    <Label htmlFor="name" className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_name")}
                    </Label>
                    <Input
                      id="name"
                      data-testid="booking-name-input"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={set("name")}
                      placeholder={t("f_name_ph")}
                      className="rounded-none border-mist bg-bone/50 h-12 focus-visible:ring-pine-500"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_phone")}
                    </Label>
                    <Input
                      id="phone"
                      data-testid="booking-phone-input"
                      required
                      minLength={8}
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+91 ..."
                      className="rounded-none border-mist bg-bone/50 h-12 focus-visible:ring-pine-500"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label htmlFor="age" className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_age")}
                    </Label>
                    <Input
                      id="age"
                      data-testid="booking-age-input"
                      required
                      type="number"
                      min={0}
                      max={120}
                      value={form.age}
                      onChange={set("age")}
                      placeholder={t("f_age_ph")}
                      className="rounded-none border-mist bg-bone/50 h-12 focus-visible:ring-pine-500"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_sex")}
                    </Label>
                    <Select value={form.sex} onValueChange={set("sex")}>
                      <SelectTrigger
                        data-testid="booking-sex-select"
                        aria-label="Sex"
                        className="rounded-none border-mist bg-bone/50 h-12 focus:ring-pine-500"
                      >
                        <SelectValue placeholder={t("f_sex_ph")} />
                      </SelectTrigger>
                      <SelectContent>
                        {SEX_OPTIONS.map(([v, k]) => (
                          <SelectItem key={v} value={v} data-testid={`sex-option-${v.toLowerCase()}`}>
                            {t(k)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2.5">
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_email")}
                    </Label>
                    <Input
                      id="email"
                      data-testid="booking-email-input"
                      required
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      className="rounded-none border-mist bg-bone/50 h-12 focus-visible:ring-pine-500"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label htmlFor="preferred_date" className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_date")}
                    </Label>
                    <Input
                      id="preferred_date"
                      data-testid="booking-date-input"
                      required
                      type="date"
                      value={form.preferred_date}
                      onChange={set("preferred_date")}
                      className="rounded-none border-mist bg-bone/50 h-12 focus-visible:ring-pine-500"
                    />
                  </div>
                  <div className="space-y-2.5">
                    <Label className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_time")}
                    </Label>
                    <Select value={form.time_slot} onValueChange={set("time_slot")}>
                      <SelectTrigger
                        data-testid="booking-timeslot-select"
                        aria-label="Preferred time slot"
                        className="rounded-none border-mist bg-bone/50 h-12 focus:ring-pine-500"
                      >
                        <SelectValue placeholder={t("f_time_ph")} />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map(([v, k, slug]) => (
                          <SelectItem key={v} value={v} data-testid={`timeslot-option-${slug}`}>
                            {t(k)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2.5">
                    <Label className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_concern")}
                    </Label>
                    <Select value={form.concern} onValueChange={set("concern")}>
                      <SelectTrigger
                        data-testid="booking-concern-select"
                        aria-label="Concern"
                        className="rounded-none border-mist bg-bone/50 h-12 focus:ring-pine-500"
                      >
                        <SelectValue placeholder={t("f_concern_ph")} />
                      </SelectTrigger>
                      <SelectContent>
                        {CONCERNS.map(([v, k]) => (
                          <SelectItem key={v} value={v} data-testid={`concern-option-${v.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            {t(k)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2.5 sm:col-span-2">
                    <Label htmlFor="message" className="text-xs uppercase tracking-[0.16em] font-semibold text-pine-900">
                      {t("f_message")}
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="booking-message-textarea"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder={t("f_message_ph")}
                      className="rounded-none border-mist bg-bone/50 focus-visible:ring-pine-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit-button"
                  className="group mt-10 w-full sm:w-auto bg-pine text-bone font-semibold text-sm tracking-wide px-10 py-4 rounded-full inline-flex items-center justify-center gap-2 hover:bg-pine-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {t("f_submitting")}
                    </>
                  ) : (
                    <>
                      {t("f_submit")}
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="mt-5 text-xs text-sage leading-relaxed">
                  {t("f_note", { email: DOCTOR.email })}
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Book;
